import {
  determineSelectedManagementOption,
  determineSelectedOwnerRepOption,
  isNullUndefinedOrEmptyObject,
} from '../../../common/utils';
import {
  mapBulkRenewalFlowConfigToFlowSteps,
  mapRenewalFlowConfigToFlowSteps,
} from '../flow/flowThunks';
import {
  setPaymentFees,
  setPaymentID,
  setSignatoryName,
} from '../payment/paymentSlice';
import {
  resetDocumentUploadProgressStatus,
  resetRenewState,
  setDocumentUploadCompleted,
  setDocumentUploadError,
  setDocumentUploadWorking,
  setInitiatingRenewFlowComplete,
  setInitiatingRenewFlowInProgress,
  setLateRenewalData,
  setRenewApplicationNumber,
  setRenewLicenseCompleted,
  setRenewLicenseError,
  setRenewLicenseInProgress,
  updatePermitData,
} from './renewSlice';
import { getCustomBreakdown } from '../../utils';
import { AppThunk } from 'src/store/store';
import api from 'src/api';
import { checkPaymentRequired } from 'src/common/licenseUtils';
import { ExpirationApplicationQuestion } from 'src/types/configuration';
import {
  ApplicationData,
  PropertyDetails,
  UploadConfiguration,
} from 'src/types/flow';
import { Person } from 'src/types/person';

export const initiateRenewFlow = (
  applicationNumber: string,
  originPath: string,
  onSuccess: (path?: string) => void
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    try {
      await dispatch(resetRenewState());
      dispatch(setInitiatingRenewFlowInProgress());
      dispatch(setRenewApplicationNumber(applicationNumber));

      const { data } = await api.getLicenseAsync(getState(), applicationNumber);

      const lateRenewalFee =
        getState()?.config?.settings?.express?.registration?.renew
          ?.late_renewal_fee;

      // If there is a late renewal fee, make api call to get required info for text on confirmation page
      if (lateRenewalFee) {
        const { data: lookupData } = await api.getLookupLicenseAsync(
          getState(),
          data?.license
        );
        const lateRenewalData = {
          hasExpired: lookupData.hasExpired,
          expirationDate: lookupData.expirationDate,
          renewBy: lookupData.renewBy,
        };
        dispatch(setLateRenewalData(lateRenewalData));
      }

      // make sure the data returned has the correct properties
      if (data.registrantType) {
        const lookupResult = await api.lookupAPNAddress(data.apn);
        let isResidential = false;
        if (lookupResult) {
          isResidential = lookupResult?.parcelSpecificInfo?.residential;
        }
        // get supplementalconfig for each flow
        const supplementalQuestionsConfigApply =
          getState()?.config?.settings?.express?.registration?.application
            ?.questions;
        const supplementalQuestionsConfigUpdate =
          getState()?.config?.settings?.express?.registration?.update
            ?.questions;
        const supplementalQuestionsConfigRenew =
          getState()?.config?.settings?.express?.registration?.renew?.questions;

        const supplementalQuestions = data?.misc?.supplementalQuestions;

        const hasAnswer = (questionKey: string): boolean => {
          return (
            supplementalQuestions?.[questionKey]?.answer !== null &&
            supplementalQuestions?.[questionKey]?.answer !== undefined
          );
        };

        // combine configs
        // We need to do this becuase when we call /renew we need to send through the supplemental questions, otherwise they are cleared
        // put renew flow last since it should be the highest priority
        // the pages (property details, confirmation) where this is used will only display the appropriate questions
        // Don't include questions from other flows if they do not have an answer (if we include them api request will fail)
        const filteredSupplementalQuestionsConfig = [
          ...(supplementalQuestionsConfigApply || []).filter((q) =>
            hasAnswer(q.key as string)
          ),
          ...(supplementalQuestionsConfigUpdate || []).filter((q) =>
            hasAnswer(q.key as string)
          ),
          ...(supplementalQuestionsConfigRenew || []),
        ];

        const uniqueSupplementalQuestions =
          filteredSupplementalQuestionsConfig.reduce(
            (
              acc: ExpirationApplicationQuestion[],
              current: ExpirationApplicationQuestion
            ) => {
              const existingIndex = acc.findIndex(
                (item) => item.key === current.key
              );
              // If key does not exist add it else replace existing value
              if (existingIndex === -1) {
                acc.push(current);
              } else {
                acc[existingIndex] = current;
              }
              return acc;
            },
            []
          );

        const supplementalQuestionResults = data.misc?.supplementalQuestions;

        const propertyDetails: PropertyDetails = {};

        if (
          !isNullUndefinedOrEmptyObject(filteredSupplementalQuestionsConfig)
        ) {
          propertyDetails.supplementalQuestions = {};

          uniqueSupplementalQuestions.forEach((question) => {
            const key = question.key as string;

            // Populate supplemental questions object
            (propertyDetails.supplementalQuestions as Record<string, unknown>)[
              key
            ] = {
              question: question.title,
              questionSubtext: question.description,
              ref: key,
              answer: supplementalQuestionResults?.[key]?.answer,
            };

            // Populate top level fields
            propertyDetails[key] = supplementalQuestionResults?.[key]?.answer;
          });
        }

        const selectedManagementOption = determineSelectedManagementOption(
          data.people
        );
        const selectedOwnerRepOption = determineSelectedOwnerRepOption(
          data.people
        );

        const ownersArray = Array.isArray(data.people.owner)
          ? data.people.owner
          : [data.people.owner];
        const userEmail = getState().auth?.cognitoEmailAddress;
        const isOwner = ownersArray?.some(
          (item: Person) => item.email === userEmail
        );
        const parsedData: Partial<ApplicationData> = {
          property: {
            propertyaddress: data.address,
            propertyid: data.apn,
            unitnumber: data.unitNumber,
            rentalstructure: data.rentalStructure,
            isResidential,
          },
          registrant: {
            registrantType: data.registrantType,
            owner: Array.isArray(data.people.owner)
              ? data.people.owner
              : [data.people.owner], // // Pages expect this to be an array, but towns that don't allows multiple owners return this as a single object
            registrant: data.people.registrant,
          },
          contacts: {
            selectedManagementOption,
            selectedOwnerRepOption,
            propertyManager: data.people?.propertyManager,
            localContact: data.people?.localContact,
          },
          propertyDetails,
          tenant: data.people?.tenant,
        };

        dispatch(updatePermitData(parsedData));
        dispatch(
          mapRenewalFlowConfigToFlowSteps(onSuccess, originPath, isOwner)
        );
        dispatch(setInitiatingRenewFlowComplete(''));
      } else {
        dispatch(
          setInitiatingRenewFlowComplete(
            'Unable to initiate renew flow due to missing data. Please contact Deckard.'
          )
        );
      }
    } catch (err) {
      dispatch(
        setInitiatingRenewFlowComplete(
          'Something went wrong while fetching required data.'
        )
      );
    }
  };
};

export const initiateBulkRenewFlow = (
  originPath: string,
  onSuccess: () => void
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    try {
      await dispatch(resetRenewState());
      dispatch(setInitiatingRenewFlowInProgress());
      dispatch(mapBulkRenewalFlowConfigToFlowSteps(onSuccess, originPath));
      dispatch(setInitiatingRenewFlowComplete(''));
    } catch (err) {
      dispatch(
        setInitiatingRenewFlowComplete(
          'Something went wrong while fetching required data.'
        )
      );
    }
  };
};

export const renewLicense = (
  onCompletionCallback: (
    wasSuccesfull: boolean,
    requiresPayment: boolean
  ) => void
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    let wasSuccesfull = true;
    let requiresPayment = false;
    let hasDocumentUploadError = false;

    try {
      dispatch(setRenewLicenseInProgress());

      // GET data from Redux
      const applicationNumber = getState().renew?.applicationNumber;
      const documents = getState().renew?.applicationData?.documents;

      const certifications = getState().renew?.applicationData?.certifications;

      // RENEW LICENSE
      await api.renewLicense(getState());

      // CALCULATE PAYMENT
      const paymentCalculation =
        await api.calculateLicenseRenwalFee(applicationNumber);
      const {
        paymentId,
        summary,
        ccFee,
        achFee,
        ccTotal,
        achTotal,
        checkFee,
        checkTotal,
        details,
      } = paymentCalculation;

      const customBreakdown = getCustomBreakdown(details);
      const renewConfig =
        getState()?.config?.settings?.express?.registration.renew;

      requiresPayment = checkPaymentRequired(renewConfig);
      const licenseVerbiage =
        getState()?.config?.settings?.express?.registration?.type_verbiage;

      if (summary.total === 0) {
        requiresPayment = false;
      }

      let breakdown = [];

      // include a late fees line item if renewal is late
      if (summary.latePenalties && summary.latePenalties > 0) {
        const licenseFeeExcludingLateFees =
          summary.total - summary.latePenalties;
        if (customBreakdown.length > 0) {
          breakdown = customBreakdown;
        } else {
          breakdown = [
            {
              key: 'licenseFee',
              label: `${licenseVerbiage} Fee`,
              value: licenseFeeExcludingLateFees,
            },
          ];
        }
        if (!breakdown.find((b) => b.key === 'lateFee')) {
          breakdown.push({
            key: 'latePenalties',
            label: 'Late Renewal Fee',
            value: summary.latePenalties,
          });
        }
      } else {
        if (customBreakdown.length > 0) {
          breakdown = customBreakdown;
        } else {
          breakdown.push({
            key: 'licenseFee',
            label: `${licenseVerbiage} Fee`,
            value: summary.total,
          });
        }
      }

      const paymentFee = {
        ccFee,
        ccTotal,
        achFee,
        achTotal,
        checkFee,
        checkTotal,
        breakdown,
        summary,
      };

      dispatch(setPaymentID(paymentId));
      dispatch(setPaymentFees(paymentFee));
      dispatch(setSignatoryName(certifications?.signatory));

      // UPLOAD DOCUMENTS
      if (documents) {
        try {
          // Each type of document may have zero or more associated files
          // but requires only one upload token per type
          // So we will store the configuration details for each document type in an array
          const uploadConfiguration: Array<UploadConfiguration> = [];

          // Get a list of the document types (documents are stored in Redux with the document type as their key)
          const documentTypes = Object.keys(documents);
          // Upload the documents using the new license numberDa
          for (const documentType in documentTypes) {
            const docTypeName = documentTypes[documentType];
            // Get an upload token for each document type
            const { uploadToken } = await api.fetchDocumentUploadToken(
              applicationNumber,
              docTypeName,
              {},
              getState()
            );

            // Store the token and associated files in the array as its own config object
            uploadConfiguration.push({
              document: documentType,
              uploadToken: uploadToken,
              files: documents[docTypeName],
            });
          }

          if (
            !uploadConfiguration ||
            uploadConfiguration.length === 0 ||
            uploadConfiguration.filter(
              (config) => config.files.attachedFiles.length === 0
            ).length === uploadConfiguration.length
          ) {
            // If there is no uploadConfiguration, it's empty or it only contains configs with no associated files, then we're done.
            dispatch(setDocumentUploadCompleted(true));
          } else {
            dispatch(setDocumentUploadWorking(true));
            // Do the upload
            for (const uploadConfig of uploadConfiguration) {
              const { files } = uploadConfig;
              const { attachedFiles } = files;
              for (const documentFile of attachedFiles) {
                const { blobFile } = documentFile;
                await api.uploadDocument({
                  uploadToken: uploadConfig.uploadToken,
                  file: blobFile,
                });
              }
            }
          }
        } catch (err) {
          wasSuccesfull = false;
          hasDocumentUploadError = true;
        }
        if (hasDocumentUploadError) {
          dispatch(
            setDocumentUploadError(
              'An unexpected error occurred while attempting to upload documents.'
            )
          );
        }
      }
    } catch (err) {
      dispatch(resetDocumentUploadProgressStatus());
      dispatch(
        setRenewLicenseError(
          'An unexpected error occurred while attempting to renew.'
        )
      );
      wasSuccesfull = false;
    } finally {
      dispatch(setDocumentUploadCompleted(true));
      dispatch(setRenewLicenseCompleted());
      onCompletionCallback(wasSuccesfull, requiresPayment);
    }
  };
};

export const bulkRenewLicenses = (
  onCompletionCallback: (error: boolean, requiresPayment: boolean) => void
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    let requiresPayment = true;
    const certifications = getState().renew?.applicationData?.certifications;

    try {
      dispatch(setRenewLicenseInProgress());

      // RENEW LICENSE
      const { licenseNumbers } = await api.bulkRenewLicenses(getState());

      // CALCULATE PAYMENT
      const paymentCalculation = await api.calculateBulkLicenseRenewalFee(
        licenseNumbers,
        getState()
      );
      const { paymentId, summary, ccFee, achFee, ccTotal, achTotal } =
        paymentCalculation;

      const licenseVerbiage =
        getState()?.config?.settings?.express?.registration?.type_verbiage;

      requiresPayment = checkPaymentRequired(
        getState()?.config?.settings?.express?.registration?.renew
      );

      if (summary.total === 0) {
        requiresPayment = false;
      }

      const paymentFee = {
        ccFee,
        ccTotal,
        achFee,
        achTotal,
        breakdown: [
          {
            key: 'licenseFee',
            label: `${licenseVerbiage} Fee`,
            value: summary.total,
          },
        ],
      };

      dispatch(setPaymentID(paymentId));
      dispatch(setPaymentFees(paymentFee));
      dispatch(setSignatoryName(certifications?.signatory));
      dispatch(setRenewLicenseCompleted());
      onCompletionCallback(true, requiresPayment);
    } catch (err) {
      dispatch(setRenewLicenseCompleted());
      onCompletionCallback(false, false);
    }
  };
};
